import { useId } from 'react';

import { PeriodType } from '@/lib/api/generated/model';
import type { PeriodBounds } from '@/lib/api/generated/model';
import { PERIOD_LABEL } from '../periodCopy';
import { currentMonthKey, thisWeekMondayKey, todayKey } from '../periodKeys';

export interface PeriodTypeSelectorProps {
  /**
   * The period being read — from the fetched response, never tracked separately. Its
   * type is the checked option; its start is where a switch to another type lands, so
   * September read by week opens on September's first week rather than today's.
   */
  period: PeriodBounds;
  /** Fired with the request key for the chosen type, at the period being read. */
  onChange: (periodKey: string | undefined) => void;
  disabled?: boolean;
}

const OPTIONS = [
  PeriodType.daily,
  PeriodType.weekly,
  PeriodType.monthly,
  PeriodType.none,
] as const;

/**
 * Which granularity of usage to view (spec 093 FR-001, FR-002).
 *
 * Same segmented-radio-group pattern as `organization-admin/PeriodField` — a set of radios
 * with visible label pills, so the semantics an assistive technology sees are the real ones.
 * Differs in intent (choosing what to *view*, not setting a limit's cadence) and in being
 * fully derived from the fetched `period` rather than a controlled value the caller owns as its
 * own state — the caller's "state" here is just the last-fetched response (FR-005).
 */
export function PeriodTypeSelector({
  period,
  onChange,
  disabled = false,
}: PeriodTypeSelectorProps) {
  const name = useId();
  const activePeriodType = period.period_type;
  const legendId = `${name}-legend`;

  return (
    <div className="flex flex-col gap-1.5">
      <span id={legendId} className="sr-only">
        Period type
      </span>
      <div
        role="radiogroup"
        aria-labelledby={legendId}
        className="border-border bg-muted/40 flex overflow-hidden rounded-md border"
      >
        {OPTIONS.map((option) => {
          const optionId = `${name}-${option}`;
          const isActive = activePeriodType === option;
          return (
            <span key={option} className="relative flex-1">
              <input
                type="radio"
                id={optionId}
                name={name}
                value={option}
                checked={isActive}
                disabled={disabled}
                onChange={() => {
                  if (isActive) return;
                  onChange(requestKeyFor(option, anchorOf(period)));
                }}
                className="peer absolute inset-0 opacity-0"
              />
              <label
                htmlFor={optionId}
                className={`peer-checked:bg-card peer-checked:text-primary peer-focus-visible:ring-ring block cursor-pointer rounded-sm px-2 py-1.5 pointer-coarse:py-3 text-center text-sm font-medium whitespace-nowrap transition-colors peer-checked:font-semibold peer-checked:shadow-raised peer-focus-visible:ring-3 peer-focus-visible:outline-none ${disabled ? 'cursor-not-allowed opacity-60' : 'hover:text-foreground text-muted-foreground'}`}
              >
                {PERIOD_LABEL[option]}
              </label>
            </span>
          );
        })}
      </div>
    </div>
  );
}

/** The request key for a period type's current instance (data-model.md's key-shape table). */
/**
 * The day a switch of period type keeps. The period being read is current — or is the
 * lifetime total, which has no date of its own — so it is today; otherwise a day of the
 * period, so a past month read by day or by week stays in that month.
 */
function anchorOf(period: PeriodBounds): Date {
  if (period.is_current || period.period_type === PeriodType.none) {
    return new Date();
  }
  const start = new Date(period.starts_at);
  // A week is the month that holds most of it — its Thursday, as ISO weeks count
  // them — so the week of 31 August goes back to September, not August.
  return period.period_type === PeriodType.weekly
    ? new Date(start.getTime() + 3 * 24 * 60 * 60 * 1000)
    : start;
}

function requestKeyFor(
  periodType: PeriodType,
  anchor: Date,
): string | undefined {
  const now = new Date();
  switch (periodType) {
    case PeriodType.daily:
      return todayKey(anchor);
    case PeriodType.weekly:
      return thisWeekMondayKey(anchor);
    case PeriodType.none:
      return PeriodType.none;
    case PeriodType.monthly: {
      // The current month is the address with no period at all, as the stepper has it.
      const month = currentMonthKey(anchor);
      return month === currentMonthKey(now) ? undefined : month;
    }
  }
}
